import { useEffect, useRef } from 'react';
import closeIcon from '../../assets/close.svg';
import { Divider } from '../elements/Divider';
import CustomInput from '../elements/CustomInput';
import SelectInput from '../elements/SelectInput';
import { convertProductClassesToSelectOptions } from '../../helper/enum-parser';
import { z as zod } from 'zod';
import { FieldValues, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, ButtonVariant } from '../elements/Button';
import { ProductClassesData } from '../../types/location';
import api from '../../lib/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiEndpoint } from '../../constants/endpoints';
import Loader from '../elements/Loader';
import { ToastHelper, ToastMessageType, ToastType } from '../../helper/toast';
import { queryClient } from '../../query-client';
import { ApiErrorResponse } from '../../types/api';

interface AddTaxRateModalProps {
  handleOpenAddTaxRateModal: () => void;
  partnerId: string;
}

const AddTaxRateModal = ({
  handleOpenAddTaxRateModal,
  partnerId,
}: AddTaxRateModalProps) => {
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const { data: productClassesDataResponse, isLoading } = useQuery<
    ProductClassesData,
    ApiEndpoint
  >({
    queryKey: ['get_product_classes'],
    queryFn: () =>
      api.fetch(`get_product_classes`, {}) as unknown as ProductClassesData,
    retry: 0,
  });

  const Schema = zod
    .object({
      name: zod.string(),
      category: zod.string(),
    })
    .superRefine(({ name, category }, ctx) => {
      const categoryName = productClassesDataResponse?.data.find(
        (productClass: ProductClassesData['data'][number]) =>
          productClass.attributes.slug === category,
      )?.attributes.name;

      const taxRateSuffix = ` - ${categoryName}`;
      const taxRateName = `${name}${taxRateSuffix}`;
      const maxCharacterCount = 50 - taxRateSuffix.length;

      if (taxRateName.length > 50) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: `Tax name can't contain more than ${maxCharacterCount} characters.`,
          path: ['name'],
        });
      }
    });

  type FormSchemaType = zod.infer<typeof Schema>;

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<FormSchemaType>({
    resolver: zodResolver(Schema),
    mode: 'all',
  });

  const addTaxRateData = useMutation({
    mutationFn: (data: FieldValues) =>
      api.fetch('add_tax_rate', {
        name: data.name,
        category: data.category,
        partnerId: partnerId,
      }),
    onSuccess: () => {
      ToastHelper.showToast(
        'Tax Rate',
        ToastType.SUCCESS,
        ToastMessageType.UPLOAD,
      );
      queryClient.refetchQueries({
        queryKey: [
          'get_default_tax_product_class',
          'tax_rate,product_class',
          1,
          100,
          partnerId,
        ],
      });

      handleOpenAddTaxRateModal();
    },
    onError: (err: ApiErrorResponse) => {
      ToastHelper.showToast(
        'Tax Rate',
        ToastType.ERROR,
        ToastMessageType.ERROR,
        err?.response?.data?.error && err.response.data.error,
      );
    },
  });

  const onSubmit = (data: FieldValues) => {
    addTaxRateData.mutate(data);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalContainerRef.current && event.target instanceof Node) {
        if (
          modalContainerRef.current &&
          !modalContainerRef.current.contains(event.target)
        ) {
          handleOpenAddTaxRateModal();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <div className='fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-50 flex justify-center items-center z-50'>
      <div
        ref={modalContainerRef}
        className='bg-white p-8 rounded-md w-[742px] h-fit'
        data-test-id='add-tax-rates-modal'
      >
        {isLoading ? (
          <div className='flex justify-center items-center w-full h-[366px]'>
            <Loader fillColor='#6923F4' />
          </div>
        ) : (
          <div className='w-full h-full flex-col'>
            <div className='w-full h-fit'>
              <p className='text-xs leading-tight text-left text-gray-600 pb-2'>
                *The name you enter will be appended with the category that you
                select below.
              </p>
            </div>
            {watch('category') === 'credits' && (
              <div className='w-full h-fit'>
                <p
                  className='text-xs leading-tight text-left text-gray-600 pb-2'
                  data-test-id='credits-info'
                >
                  *Tax rates configured for credits will set the tax rate for
                  credits, email credit gift cards, and physical credit gift
                  cards.
                </p>
              </div>
            )}
            <div className='w-full flex justify-between'>
              {' '}
              <h1>Add Tax Rate</h1>
              <img
                onClick={() => {
                  handleOpenAddTaxRateModal();
                }}
                src={closeIcon}
                width={10}
                alt='close-icon'
                className='cursor-pointer'
                data-test-id='close-icon'
              />
            </div>
            <Divider customStyle='mt-5' />
            <div className='flex-col w-full pt-6 pb-6'>
              <CustomInput
                id='name'
                label='Name'
                {...register('name')}
                error={errors['name']?.message}
              />
              <SelectInput
                label='Category'
                options={convertProductClassesToSelectOptions(
                  productClassesDataResponse || { data: [] },
                )}
                value={watch('category')}
                {...register('category')}
              />
            </div>
            <div className='flex justify-between'>
              <Button
                label='Cancel'
                onClick={() => {
                  handleOpenAddTaxRateModal();
                }}
                className='w-[87px] h-[40px] rounded-[39px]'
                variant={ButtonVariant.Ghost}
                dataTestId='cancel-button'
              />
              <Button
                variant={ButtonVariant.Default}
                onClick={handleSubmit(onSubmit)}
                label={'Save'}
                disabled={
                  !watch('name') ||
                  !watch('category') ||
                  !!Object.keys(errors).length
                }
                className='w-[70px] h-[40px] rounded-[39px]'
                loading={addTaxRateData.isPending}
                dataTestId='submit-button'
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddTaxRateModal;
